import { getAuth } from 'firebase/auth';

const API_URL = process.env.REACT_APP_API_URL;

// Debug log for API_URL
console.log('stripeService: API_URL loaded:', API_URL);
if (!API_URL) {
  console.error('stripeService: REACT_APP_API_URL environment variable is not set!');
}

/**
 * Create a Stripe checkout session
 * @param {Object} options - Optional configuration for the checkout session
 * @param {number} options.trialPeriodDays - Free trial period (default: 7)
 * @param {Object} options.customerData - Additional customer info
 * @returns {Promise<Object>} - Checkout session data with URL
 */
export const createCheckoutSession = async (options = {}) => {
  try {
    // Get Firebase ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be authenticated to create checkout session');
    }
    
    const idToken = await user.getIdToken();
    
    console.log('stripeService: Creating checkout session with options:', options);
    
    const response = await fetch(`${API_URL}/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        options: {
          // trialPeriodDays: 7,  // Default 7-day trial
          ...options
        }
      })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error('Stripe checkout creation failed:', response.status, result);
      throw new Error(result.error || `Failed to create checkout session: ${response.statusText}`);
    }
    
    if (!result.success) {
      console.error('Stripe checkout creation failed:', result.error);
      throw new Error(result.error || 'Failed to create checkout session');
    }
    
    console.log('stripeService: Checkout session created successfully:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('stripeService createCheckoutSession error:', error);
    throw error;
  }
};

/**
 * Verify a Stripe payment session
 * @param {string} sessionId - The Stripe session ID to verify
 * @returns {Promise<Object>} - Verification result with payment and subscription data
 */
export const verifyPaymentSession = async (sessionId) => {
  try {
    console.log('stripeService: Verifying payment session:', sessionId);
    
    // Get Firebase ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be authenticated to verify payment session');
    }
    
    const idToken = await user.getIdToken();
    
    const response = await fetch(`${API_URL}/stripe/verify-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ sessionId })
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error('Stripe session verification failed:', response.status, result);
      throw new Error(result.error || `Failed to verify session: ${response.statusText}`);
    }
    
    if (!result.success) {
      console.error('Stripe session verification failed:', result.error);
      throw new Error(result.error || 'Payment verification failed');
    }
    
    console.log('stripeService: Session verified successfully:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('stripeService verifyPaymentSession error:', error);
    throw error;
  }
};

/**
 * Get current subscription status for authenticated user
 * @returns {Promise<Object>} - Subscription status data
 */
export const getSubscriptionStatus = async () => {
  try {
    // Get Firebase ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be authenticated to check subscription status');
    }
    
    const idToken = await user.getIdToken();
    
    console.log('stripeService: Getting subscription status');
    
    const response = await fetch(`${API_URL}/stripe/subscription-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error('Subscription status check failed:', response.status, result);
      throw new Error(result.error || `Failed to get subscription status: ${response.statusText}`);
    }
    
    if (!result.success) {
      console.error('Subscription status check failed:', result.error);
      throw new Error(result.error || 'Failed to get subscription status');
    }
    
    console.log('stripeService: Subscription status retrieved:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('stripeService getSubscriptionStatus error:', error);
    throw error;
  }
};

/**
 * Cancel the current subscription for authenticated user
 * @returns {Promise<Object>} - Cancellation result
 */
export const cancelSubscription = async () => {
  try {
    // Get Firebase ID token
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('User must be authenticated to cancel subscription');
    }
    
    const idToken = await user.getIdToken();
    
    console.log('stripeService: Canceling subscription');
    
    const response = await fetch(`${API_URL}/stripe/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      }
    });
    
    const result = await response.json();
    
    if (!response.ok) {
      console.error('Subscription cancellation failed:', response.status, result);
      throw new Error(result.error || `Failed to cancel subscription: ${response.statusText}`);
    }
    
    if (!result.success) {
      console.error('Subscription cancellation failed:', result.error);
      throw new Error(result.error || 'Failed to cancel subscription');
    }
    
    console.log('stripeService: Subscription canceled successfully:', result.data);
    return result.data;
    
  } catch (error) {
    console.error('stripeService cancelSubscription error:', error);
    throw error;
  }
};
